import { getStageForLead } from "./pipelineStages";
import { isLeadClosed, isOverdue, primaryContactValue } from "./utils";

export const APP_TIMEZONE = "America/Mexico_City";

/** Un contacto corto: marcar, hablar y dejar la nota. */
export const MINUTES_PER_TOUCH = 3;

export function workMinutes(count) {
  return Math.max(0, Math.round(Number(count) || 0)) * MINUTES_PER_TOUCH;
}

export function formatWorkTime(minutes) {
  const n = Math.max(0, Math.round(Number(minutes) || 0));
  if (n <= 0) return "0 min";
  if (n < 60) return `${n} min`;
  const hours = Math.floor(n / 60);
  const rest = n % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function weekdayIndex(date = new Date()) {
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: APP_TIMEZONE, weekday: "short" }).format(date);
  return { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4 }[wd] ?? 5;
}

/** Contactos que faltan para la meta semanal. */
export function goalLeft(done, target) {
  return Math.max(0, Math.round(Number(target) || 0) - Math.round(Number(done) || 0));
}

/** Solo la parte de hoy: el faltante de la meta, repartido en los días hábiles que quedan. */
export function todayGoalCount(done, target, date = new Date()) {
  const left = goalLeft(done, target);
  if (!left) return 0;
  const index = weekdayIndex(date);
  const daysLeft = index <= 4 ? 5 - index : 1;
  return Math.min(left, Math.ceil(left / daysLeft));
}

export function goalEffortLine(done, target) {
  const n = todayGoalCount(done, target);
  if (!n) return "Meta lista";
  return n === 1 ? "1 restante hoy" : `${n} restantes hoy`;
}

const RANK = { overdue: 0, urgent: 1, today: 2, nuevo: 3, closing: 4, open: 5 };

const BUCKET_LABEL = {
  overdue: "Vencido",
  urgent: "Urgente",
  today: "Para hoy",
  nuevo: "Primer contacto",
  closing: "En cierre",
  open: "Sin siguiente paso",
};

export function todayKey(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function greeting(date = new Date()) {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: APP_TIMEZONE,
      hour: "numeric",
      hour12: false,
    }).format(date)
  );
  if (hour < 12) return "Buenos días";
  if (hour < 19) return "Buenas tardes";
  return "Buenas noches";
}

export function todayLabel(date = new Date()) {
  const label = new Intl.DateTimeFormat("es-MX", {
    timeZone: APP_TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function phoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

export function waLink(value) {
  const digits = phoneDigits(value);
  if (!digits) return "";
  const full = digits.length === 10 ? `52${digits}` : digits;
  return `https://wa.me/${full}`;
}

function channels(lead) {
  const mobile = primaryContactValue(lead, "MOBILE");
  const phone = primaryContactValue(lead, "PHONE") || mobile;
  const whatsapp = primaryContactValue(lead, "WHATSAPP");
  const email = primaryContactValue(lead, "EMAIL");
  return { whatsapp, phone, email };
}

function suggest(lead, bucket) {
  const stage = getStageForLead(lead);
  const { whatsapp, phone, email } = channels(lead);
  const contact = lead.contactName ? ` con ${lead.contactName}` : "";

  if (bucket === "overdue") {
    return {
      verb: "Retoma el contacto",
      detail: `Llama${contact}, deja la nota y la siguiente fecha.`,
      type: phone ? "CALL" : whatsapp ? "WHATSAPP" : "EMAIL",
    };
  }
  if (bucket === "urgent") {
    return {
      verb: "Un contacto corto",
      detail: `Es urgente. Escríbele o llámale${contact} ahora.`,
      type: phone ? "CALL" : "WHATSAPP",
    };
  }
  if (stage.key === "nuevo") {
    return {
      verb: "Primer contacto",
      detail: `Preséntate${contact}, confirma interés y deja la siguiente fecha.`,
      type: phone ? "CALL" : email ? "EMAIL" : "WHATSAPP",
    };
  }
  if (stage.key === "qualified") {
    return {
      verb: "Agenda la demo",
      detail: `Ya hay interés. Propón una fecha${contact} y regístrala.`,
      type: phone ? "CALL" : "WHATSAPP",
    };
  }
  if (stage.key === "demo") {
    return {
      verb: "Confirma la demo",
      detail: `Confirma que sigue en pie${contact} y deja el siguiente paso.`,
      type: "PRESENTATION",
    };
  }
  if (stage.key === "closing") {
    return {
      verb: "Cierra hoy",
      detail: `Pregunta la decisión${contact} y deja qué sigue.`,
      type: phone ? "CALL" : "EMAIL",
    };
  }
  if (bucket === "today") {
    return {
      verb: "Seguimiento de hoy",
      detail: `Escribe o llama${contact}. Anota qué dijeron y cuándo vuelves.`,
      type: whatsapp && !phone ? "WHATSAPP" : "CALL",
    };
  }
  return {
    verb: "Deja la siguiente fecha",
    detail: `Contacta${contact} y agenda cuándo vuelves.`,
    type: phone ? "CALL" : "WHATSAPP",
  };
}

function add(map, lead, bucket) {
  if (!lead?._id || isLeadClosed(lead)) return;
  const id = String(lead._id);
  const prev = map.get(id);
  if (!prev || RANK[bucket] < RANK[prev.bucket]) {
    map.set(id, { lead, bucket });
  }
}

/** Cola del día: vencidos, urgentes, hoy, primer contacto, cierre y leads sin fecha. */
export function buildDayQueue(pending, leads = []) {
  const map = new Map();
  for (const lead of pending?.overdue || []) add(map, lead, "overdue");
  for (const lead of pending?.urgent || []) add(map, lead, "urgent");
  for (const lead of pending?.today || []) add(map, lead, "today");

  const claimed = new Set(map.keys());
  for (const lead of leads) {
    if (!lead?._id || claimed.has(String(lead._id)) || isLeadClosed(lead)) continue;
    const stage = getStageForLead(lead).key;
    if (stage === "won" || stage === "lost") continue;
    if (isOverdue(lead.nextFollowUpAt)) add(map, lead, "overdue");
    else if (stage === "nuevo") add(map, lead, "nuevo");
    else if (stage === "closing") add(map, lead, "closing");
    else if (!lead.nextFollowUpAt) add(map, lead, "open");
  }

  return [...map.values()]
    .sort((a, b) => {
      const rank = RANK[a.bucket] - RANK[b.bucket];
      if (rank) return rank;
      return (b.lead.priority === "URGENT") - (a.lead.priority === "URGENT");
    })
    .map(({ lead, bucket }) => ({
      id: String(lead._id),
      lead,
      bucket,
      bucketLabel: BUCKET_LABEL[bucket],
      stageLabel: getStageForLead(lead).label,
      ...suggest(lead, bucket),
      ...channels(lead),
    }));
}

export function queueSummary(items = []) {
  const count = (bucket) => items.filter((item) => item.bucket === bucket).length;
  return {
    total: items.length,
    overdue: count("overdue"),
    urgent: count("urgent"),
    today: count("today"),
    nuevo: count("nuevo"),
    closing: count("closing"),
    open: count("open"),
  };
}
